import { sortBy } from 'lodash'
import { isValidAndNonEmpty } from '@/utils/utils'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { useModalsStore } from '@/stores/modals'
import { usePaywallGuard } from '@/composables/usePaywallGuard'
import { useI18n } from '@/composables/useI18n'

// Constantes para configuração
const TAB_CONFIG = ref({
  EXAMS: {
    value: 'exams',
    icon: 'mdi-flask-outline',
    id: 'exams',
  },
  SUMMARY: {
    icon: 'mdi-text-long',
    type: 'summary',
  },
  CUSTOM_SUMMARY: {
    icon: 'mdi-text-long',
    type: 'custom_summary',
  },
  LOADING: {
    icon: 'mdi-sync',
    type: 'loading',
  },
})

export const useConsultationStore = defineStore('consultation', () => {
  const appStore = useAppStore()
  const userStore = useUserStore()
  const modalsStore = useModalsStore()

  const { t } = useI18n()

  const { openCustomTemplateModal } = modalsStore
  const { getSpecialtyName } = appStore
  const { isProfileCompleted, isProUser } = storeToRefs(userStore)

  // Estado reativo
  const isTranscriptionModalOpen = ref(false)
  const tab = ref(null)
  const consultation = ref(null)
  const isModelApplyerModalOpen = ref(false)

  // Computed properties simplificadas
  const consultationId = computed(() => consultation.value?.id)
  const consultationTranscription = computed(
    () => consultation.value?.transcriptions?.[0]?.content,
  )
  const reports = computed(() => consultation.value?.reports)
  const exams = computed(() => consultation.value?.exams)
  const patient = computed(() => consultation.value?.patient)

  // Função auxiliar para determinar o conteúdo a ser exibido
  const getContentToDisplay = (report) => {
    return isValidAndNonEmpty(report.editedContent)
      ? report.editedContent
      : report.content
  }

  // Função auxiliar para verificar se um report está carregando
  const isReportLoading = (report) => {
    return (
      !isValidAndNonEmpty(report.content) &&
      !isValidAndNonEmpty(report.editedContent)
    )
  }

  // Seleção do report primário: primeiro (por createdAt) com conteúdo.
  // Consultas com reports duplicados vazios (race condition no finish do
  // backend) caem no report com content; fallback preserva a tab de loading
  // quando todos ainda estão em geração.
  const selectPrimaryReport = (sorted) =>
    sorted.find((report) => !isReportLoading(report)) ?? sorted[0]

  const sortedReports = computed(() => sortBy(reports.value ?? [], 'createdAt'))
  const primaryReport = computed(() =>
    selectPrimaryReport(sortedReports.value),
  )

  // Função auxiliar para criar tab de exames
  const createExamsTab = () => {
    if (!exams.value?.length) return null

    return {
      ...TAB_CONFIG.value.EXAMS,
      label: t('consultations.exams'),
      content: exams.value,
    }
  }

  // Função auxiliar para criar tab de resumo padrão
  const createSummaryTab = (report) => {
    const specialty = report.customSpecialty || report.specialty

    return {
      label: getSpecialtyName(specialty),
      value: report.id,
      icon: TAB_CONFIG.value.SUMMARY.icon,
      id: report.id,
      type: TAB_CONFIG.value.SUMMARY.type,
      isMarkdown: !!report?.content?.summary_markdown,
      content: getContentToDisplay(report),
    }
  }

  const isRemovedCustomPromptReport = (report) => {
    return !!report?.content?.custom_summary && !report?.customPrompt
  }

  const getCustomPromptTabLabel = (report) => {
    const baseLabel =
      report?.customPrompt?.title ||
      t('consultations.removedModelTabLabel', 'Modelo removido')

    if (isRemovedCustomPromptReport(report)) {
      return `${baseLabel} *`
    }

    return baseLabel
  }

  // Função auxiliar para criar tab de resumo customizado
  const createCustomSummaryTab = (report) => {
    if (!report?.content?.custom_summary) return null

    return {
      label: getCustomPromptTabLabel(report),
      value: `${report.id}_custom_summary`,
      icon: TAB_CONFIG.value.CUSTOM_SUMMARY.icon,
      id: report.id,
      type: TAB_CONFIG.value.CUSTOM_SUMMARY.type,
      content: getContentToDisplay(report),
      isMarkdown: true,
      isRemovedModel: isRemovedCustomPromptReport(report),
    }
  }

  // Função auxiliar para criar tab de carregamento
  const createLoadingTab = (report) => {
    const label = report?.customPrompt?.title
      ? getCustomPromptTabLabel(report)
      : `${getSpecialtyName(report.customSpecialty || report.specialty)}`

    return {
      label,
      value: `${report.id}_loading`,
      icon: TAB_CONFIG.value.LOADING.icon,
      id: report.id,
      type: TAB_CONFIG.value.LOADING.type,
      content: null,
      isMarkdown: false,
      isRemovedModel: isRemovedCustomPromptReport(report),
    }
  }

  // Função auxiliar para processar relatórios adicionais
  const processAdditionalReports = (additionalReports) => {
    const tabs = []

    additionalReports.forEach((report) => {
      // Verifica se o report está carregando
      if (isReportLoading(report)) {
        const loadingTab = createLoadingTab(report)
        if (loadingTab) tabs.push(loadingTab)
        return
      }

      const hasCustomSummary = !!report.customPrompt

      if (hasCustomSummary) {
        const customTab = createCustomSummaryTab(report)
        if (customTab) tabs.push(customTab)
      } else {
        const summaryTab = createSummaryTab(report)
        if (summaryTab) tabs.push(summaryTab)
      }
    })

    return tabs
  }

  // Função auxiliar para inserir tab customizada na posição correta
  // const insertCustomTabInPosition = (tabs, customTab) => {
  //   if (!customTab) return tabs

  //   const examsIndex = tabs.findIndex((tab) => tab.id === 'exams')
  //   const insertIndex = examsIndex >= 0 ? examsIndex + 1 : 0

  //   tabs.splice(insertIndex, 0, customTab)
  //   return tabs
  // }

  // Computed property refatorada para construção de tabs
  const tabsConstructor = computed(() => {
    if (!reports.value?.length) return []

    const tabs = []
    const firstReport = primaryReport.value

    // Adiciona tab de exames se existir
    const examsTab = createExamsTab()
    if (examsTab) tabs.push(examsTab)

    // Verifica se o primeiro relatório está carregando
    if (isReportLoading(firstReport)) {
      const loadingTab = createLoadingTab(firstReport)
      if (loadingTab) tabs.push(loadingTab)
    } else {
      // Adiciona tab de resumo padrão do primeiro relatório
      const summaryTab = createSummaryTab(firstReport)
      if (summaryTab) tabs.push(summaryTab)

      // Adiciona tab de resumo customizado do primeiro relatório na posição correta
      const customSummaryTab = createCustomSummaryTab(firstReport)
      if (customSummaryTab) tabs.push(customSummaryTab)

      // insertCustomTabInPosition(tabs, customSummaryTab)
    }

    // Processa relatórios adicionais (todos exceto o primário)
    const additionalReports = sortedReports.value.filter(
      (report) => report.id !== firstReport.id,
    )
    const additionalTabs = processAdditionalReports(additionalReports)
    tabs.push(...additionalTabs)

    return tabs
  })

  // Computed property para determinar a chave do conteúdo
  const contentKey = computed(() => {
    return isValidAndNonEmpty(primaryReport.value?.editedContent)
      ? 'editedContent'
      : 'content'
  })

  // Computed property para obter reports que estão carregando
  const loadingReports = computed(() => {
    if (!reports.value?.length) return []

    return reports.value.filter((report) => isReportLoading(report))
  })

  const openModelApplyerModal = (origin = null) => {
    if (!isProfileCompleted.value && !isProUser.value) {
      openCustomTemplateModal(origin)
      return false
    }

    const {
      blockIfResourceLimitReached,
      blockIfMaxTabsLimitReached,
    } = usePaywallGuard()

    if (blockIfResourceLimitReached()) return false
    if (blockIfMaxTabsLimitReached()) return false

    isModelApplyerModalOpen.value = true
    return true
  }

  return {
    // Estado
    isTranscriptionModalOpen,
    tab,
    consultation,

    // Computed properties
    consultationId,
    consultationTranscription,
    contentKey,
    primaryReport,
    reports,
    exams,
    patient,
    tabsConstructor,
    isModelApplyerModalOpen,
    loadingReports,
    openModelApplyerModal,
  }
})
